/**
 * US5/T085/T086/T090 (FR-025, FR-027a, screen-routes.md). File-picker step of the two-step
 * validate → diff → commit flow every import (catalogue or tier price) shares — the same shape
 * `007-inventory`'s stock-upload wizard uses (`inventory/uploads/List.tsx`), and unrelated to it: a
 * different feature (`001` vs `007-inventory`), a different service call
 * (the file to Files, then `postImport`/`lib/services/catalog.ts` sends its hash to `POST /imports?kind=`), and
 * — unlike that screen — this one's diff lives at a real `GET /imports/{batchId}/rows`, so nothing
 * needs an in-memory cache to survive a redirect (`upload-cache.ts` has no counterpart here).
 *
 * There is no template file to name in a header comment (constitution guard check 7's other
 * branch): this composes the template's raw dropzone primitive directly
 * (`@core/ui/upload`, `vendor/isomorphic/apps/isomorphic/src/app/shared/file-upload.tsx`'s own
 * `Upload` usage) exactly as `inventory/uploads/List.tsx` already does, past no styling-markup
 * threshold of its own.
 *
 * `ImportWizard` is exported (not just the route's default) so `T091` (the price-list bucket's
 * tier-price upload from `/tiers/[tierId]/prices`) can reuse it directly with `kind="tier_price"`
 * and its own `backHref`/`backLabel`, rather than only being reachable by linking to
 * `/imports?kind=tier_price` — the query-string form works too, since the default export below
 * just reads `kind` off the URL. Neither `ImportBatchResponse` nor `ImportRowView` carries a
 * `kind` field back from the API, so nothing downstream (the diff screen) needs to know which one
 * a batch was — the wizard is the only place `kind` exists at all.
 */
import { useState, type ChangeEvent } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { importBatchPath } from "../../lib/paths";
import { Text } from "rizzui";
import { PageHeader, ErrorSurface } from "@steelyard/theme";
import Upload from "@core/ui/upload";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { importTemplateUrl, postImport, unreadableImportFileMessage, type ImportKind } from "../../lib/services/catalog";
import { UploadDropHint, UploadBusyHint } from "../../components/upload-drop-hint";
import { useFileUpload } from "@steelyard/common";

export function ImportWizard({
  kind,
  title,
  breadcrumb,
}: {
  kind: ImportKind;
  title: string;
  breadcrumb: { name: string; href?: string }[];
}) {
  const navigate = useNavigate();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  // The extension pre-check's own refusal; the upload's or validation's is `upload.error`.
  const [error, setError] = useState<unknown>(null);
  const upload = useFileUpload("catalogue-import", {
    onUploaded: async (uploaded, file) => {
      const result = await postImport(kind, uploaded.hash, file.name);
      // An upload response without the batch's number lands on its id URL, which redirects.
      navigate(importBatchPath(result.batchNumber || result.batchId));
    },
  });
  const isSubmitting = upload.busy;

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="open the importer" />;
  }

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // T090: rejected before a byte reaches the network, with the exact text
    // `ValidateImportHandler`'s own 400 carries for a file none of its parsers can read — not a
    // paraphrase that could drift from it.
    // §8.4 / §9.2 step 5: CSV or Excel (.xlsx), the two formats the service parses.
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      event.target.value = ""; // re-picking the same file must still fire a change
      setError(new Error(unreadableImportFileMessage(file.name)));
      return;
    }

    void upload.onChange(event);
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <PageHeader title={title} breadcrumb={breadcrumb} />

      <Text className="text-gray-500">
        CSV or Excel (.xlsx, first sheet), kept under 10&nbsp;MB — a larger or unreadable file is refused with the reason named on the
        file. Nothing is written to the catalogue until you review the diff and commit it.{" "}
        <a href={importTemplateUrl(kind)} download={`steelyard-${kind}-template.csv`} className="underline">
          Download the template
        </a>
        .
      </Text>

      {error ?? upload.error ? <ErrorSurface error={error ?? upload.error} action="validate this file" /> : null}

      <Upload
        accept="csv"
        // The template's presets have no .xlsx; its input spreads these last, so this wins.
        dropzoneInputProps={{ accept: ".csv,.xlsx" }}
        multiple={false}
        disabled={isSubmitting}
        onChange={onChange}
        aria-label="Import CSV or Excel file"
        placeholderText={isSubmitting ? <UploadBusyHint label="Validating…" /> : <UploadDropHint />}
      />
    </div>
  );
}

const LABEL: Record<ImportKind, string> = { catalogue: "Catalogue Import", tier_price: "Tier-Price Import" };

/** `/imports` — `kind` defaults to `catalogue`; `?kind=tier_price` is T091's other entry point. */
export default function ImportsLandingPage() {
  const [searchParams] = useSearchParams();
  const kind: ImportKind = searchParams.get("kind") === "tier_price" ? "tier_price" : "catalogue";

  return (
    <ImportWizard
      kind={kind}
      title={LABEL[kind]}
      breadcrumb={[{ name: "Home", href: "/" }, { name: "Data" }, { name: LABEL[kind] }]}
    />
  );
}
